// Pipe17 onboarding — talks only to control-tower's /api/manage-pipe17, which
// does all the cross-service glue (pipe17-app-config + oms-config + provider doc
// + Pipe17 webhook registration) and proxies the backfill to rl-oms-be.

import axiosInstance from "../axiosInstance";

// control-tower has no dedicated Pipe17 "whoami" endpoint, so it recovers
// org_key by reading it off the first order/product/webhook record the key
// can see (manage-pipe17.class.js _probeApiKey). An account with none of
// those yet (e.g. a brand-new org being onboarded for the first time) can't
// be resolved that way, and the backend throws a BadRequest whose message
// ends in "pass org_key explicitly" — the one signal callers should key off
// of to prompt the operator for a manual org_key. Exported so every caller
// (this service's onboard() consumers) checks the same condition instead of
// each re-implementing its own regex.
export const ORG_KEY_REQUIRED_PATTERN = /pass org_key explicitly/i;
export const needsOrgKey = (message) => ORG_KEY_REQUIRED_PATTERN.test(message || "");

export const pipe17Service = {
  // Onboard a client: upserts credentials + oms-config and registers the webhook.
  // data: { client_id, api_key, org_key? } — org_key is normally auto-resolved
  // from the API key on the backend by reading it off the account's own
  // orders/products/webhooks; only send it once a prior attempt has reported
  // (via needsOrgKey) that the account has none of those yet to resolve from.
  onboard: async (data) => {
    try {
      let response = await axiosInstance.post("/manage-pipe17", data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Current onboarding state for a client (pipe17-app-config + oms-config rows).
  status: async (clientId) => {
    try {
      let response = await axiosInstance.get(
        `/manage-pipe17?client_id=${clientId}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Kick off a one-off catalog + order-history + PO backfill for the client.
  backfill: async (clientId) => {
    try {
      let response = await axiosInstance.patch(`/manage-pipe17/${clientId}`, {
        action: "backfill",
        client_id: clientId,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Pull the client's Pipe17 purchase-order history (suppliers first, then POs).
  // Separate from backfill() because re-paging the whole catalog and order
  // history just to refresh POs is far too expensive to sit behind a button on
  // the Purchase Orders page.
  //
  // Returns as soon as the paging job is QUEUED — the POs land asynchronously as
  // their inbound references drain, so the caller should tell the operator to
  // refresh shortly rather than expecting rows to exist on response.
  syncPurchaseOrders: async (clientId) => {
    try {
      let response = await axiosInstance.patch(`/manage-pipe17/${clientId}`, {
        action: "sync-purchases",
        client_id: clientId,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
